/**
 * normalizeCalendarRefs — map `Filtered*Dto` shape from
 * `/api/v1/calendar/{pilots,instructors,resources}` into the legacy shape
 * that `bridgeReducers` + `monthFilterDataAdapter` expect (`pilotID`,
 * `resourceID`, `locationID`, flat `name`/`surname`, nested `location`).
 *
 * Keeping this at the refs-slice boundary means every downstream consumer
 * (dropdowns, cascade filter, selectors) stays untouched.
 *
 * @module normalizeCalendarRefs
 */

/** FilteredPilotDto → legacy pilot shape. */
export function normalizeFilteredPilot(dto) {
	if (!dto || typeof dto !== 'object') return dto;
	const pilotId = dto.pilotId ?? dto.pilotID ?? dto.id;
	return {
		...dto,
		id: pilotId,
		pilotID: pilotId,
		name: dto.pilotName ?? dto.name ?? '',
		pilotName: dto.pilotName ?? dto.name ?? '',
		surname: dto.pilotSurname ?? dto.surname ?? '',
		pilotSurname: dto.pilotSurname ?? dto.surname ?? '',
		organizationId: dto.organizationId,
		organizationUnique: dto.organizationUnique,
		organizationName: dto.organizationName
	};
}

/**
 * FilteredInstructorDto → legacy resource shape (instructors live in the
 * `resources` bridge as type=instructor). Maintains nested `location` so
 * the cascade adapter's existing fallbacks still fire.
 */
export function normalizeFilteredInstructor(dto) {
	if (!dto || typeof dto !== 'object') return dto;
	const resourceId = dto.resourceId ?? dto.resourceID ?? dto.id;
	const locationId = dto.locationId ?? dto.locationID ?? null;
	const name = dto.instructorName ?? dto.name ?? '';
	const surname = dto.instructorSurname ?? dto.surname ?? '';
	return {
		...dto,
		id: resourceId,
		resourceID: resourceId,
		name: `${name} ${surname}`.trim() || name,
		instructorName: name,
		instructorSurname: surname,
		type: 'instructor',
		locationID: locationId,
		locationName: dto.locationName ?? '',
		location: locationId != null ? { id: locationId, name: dto.locationName ?? '' } : null,
		organizationId: dto.organizationId,
		organizationUnique: dto.organizationUnique,
		organizationName: dto.organizationName
	};
}

/** FilteredResourceDto → legacy resource shape (aircraft/sim). */
export function normalizeFilteredResource(dto) {
	if (!dto || typeof dto !== 'object') return dto;
	const resourceId = dto.resourceId ?? dto.resourceID ?? dto.id;
	const locationId = dto.locationId ?? dto.locationID ?? null;
	const name = dto.resourceName ?? dto.name ?? '';
	return {
		...dto,
		id: resourceId,
		resourceID: resourceId,
		name,
		resourceName: name,
		locationID: locationId,
		locationName: dto.locationName ?? '',
		location: locationId != null ? { id: locationId, name: dto.locationName ?? '' } : null,
		organizationId: dto.organizationId,
		organizationUnique: dto.organizationUnique,
		organizationName: dto.organizationName
	};
}

/**
 * Calendar `Location` entity from `/api/v1/calendar/locations` →
 * legacy location shape. Response contains a nested `organization: {id,
 * unique, name}` object; we flatten it onto the root so `cascadeLocations`
 * (which reads `item.organizationUnique`) and `buildGroups` (group title
 * = `organizationName`) work without extra lookups.
 */
export function normalizeCalendarLocation(dto) {
	if (!dto || typeof dto !== 'object') return dto;
	const org = dto.organization && typeof dto.organization === 'object' ? dto.organization : null;
	return {
		...dto,
		locationID: dto.locationID ?? dto.id,
		name: dto.name ?? dto.locationName ?? '',
		organizationId: dto.organizationId ?? (org && org.id) ?? null,
		organizationUnique: dto.organizationUnique ?? (org && org.unique) ?? null,
		organizationName: dto.organizationName ?? (org && org.name) ?? ''
	};
}

/**
 * Calendar `Organization` entity (ORGANIZATION_BASE_GROUP). Shape already
 * matches legacy (`{id, unique, name}`), so this is a defensive pass-through
 * that guarantees the fields the bridge reads exist even for slim payloads.
 */
export function normalizeCalendarOrganization(dto) {
	if (!dto || typeof dto !== 'object') return dto;
	return {
		...dto,
		id: dto.id,
		unique: dto.unique,
		name: dto.name
	};
}
